import parse from "html-react-parser";
import { valid as isValidHTML } from "node-html-parser";
import { useEffect, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { LuChevronDown, LuCopy, LuRotateCcw } from "react-icons/lu";
import { useSnackbar } from "../../../../../providers/SnackbarProvider";
import { typewriterWorkerService } from "../../../../../services/typewriterWorkerService";
import {
  AssistantMessageAnimationStatus,
  ProjectChatAssistantMessage,
} from "../../../../../view_stores/chat_messages/chatModels";
import { useChatMessagesActions } from "../../../../../view_stores/chat_messages/useChatMessagesStore";
import { typewriterUpdateEmitter } from "../../ProjectChatMessages";

interface ExpandableMessageProps {
  message: string;
  icon?: React.ReactNode;
  expandBtnText: {
    contentHiddenText: string;
    contentVisibleText: string;
  };
  canCopy: boolean;
}

export interface RollbackBtnProps {
  isDisabled: boolean;
  onClick: () => void;
}

interface ProjectAssistantMessageBaseProps {
  message: ProjectChatAssistantMessage;
  typeWriterAnimationSpeed: number;
  words: string[];
  renderContent: (visibleWords: number) => React.ReactNode;
  expandableMessage?: ExpandableMessageProps;
  actions?: React.ReactNode;
  rollbackBtn?: RollbackBtnProps;
}

export function ProjectAssistantMessageBase({
  message,
  typeWriterAnimationSpeed,
  words,
  renderContent,
  expandableMessage,
  actions,
  rollbackBtn,
}: ProjectAssistantMessageBaseProps) {
  const [visibleWords, setVisibleWords] = useState(message.visibleWords);
  const [isExpanded, setIsExpanded] = useState(false);
  const { onAssistantMessageAnimationFinished } = useChatMessagesActions();
  const { showSnackbar } = useSnackbar();

  // Run animation when message is in ANIMATING status
  useEffect(() => {
    if (message.animationStatus === AssistantMessageAnimationStatus.ANIMATING) {
      typewriterWorkerService.startAnimation(
        words.length,
        typeWriterAnimationSpeed,
        {
          onWordUpdate: (currentWordIndex: number) => {
            message.visibleWords = currentWordIndex;
            setVisibleWords(currentWordIndex);
            typewriterUpdateEmitter.emit();
          },
          onComplete: () => {
            // Only finish animation if message is fully generated
            if (message.fullyGenerated) {
              onAssistantMessageAnimationFinished();
            }
            message.visibleWords = words.length;
            setVisibleWords(words.length);
          },
        },
        message.visibleWords
      );
    } else if (
      message.animationStatus === AssistantMessageAnimationStatus.DISPLAYED
    ) {
      message.visibleWords = words.length;
      setVisibleWords(words.length);
    }
  }, [message.animationStatus, message.fullyGenerated, words]);

  // Cleanup animation when component unmounts or message changes
  useEffect(() => {
    return () => {
      // Only stop animation if this message is currently animating
      if (
        message.animationStatus === AssistantMessageAnimationStatus.ANIMATING
      ) {
        typewriterWorkerService.stopAnimation();
      }
    };
  }, [message.id, message.animationStatus]);

  const handleCopy = () => {
    showSnackbar("Copied to clipboard");
  };

  // Don't render anything if the message is waiting to be animated
  if (message.animationStatus === AssistantMessageAnimationStatus.WAITING) {
    return null;
  }

  return (
    <div>
      <div
        className={`${
          message.animationStatus ===
            AssistantMessageAnimationStatus.ANIMATING &&
          "message-container assistant-container"
        } w-full flex justify-start ${rollbackBtn && "mb-7"}`}
      >
        <div
          className="bg-light-surface-primary dark:bg-[#191C1F] 
          border-white dark:border-[#373B40] 
          border flex flex-col justify-start max-w-[80%] 
          rounded-md break-words hyphens-auto message-bubble
          tracking-tight relative"
        >
          <div className={`py-3 px-5 overflow-hidden ${rollbackBtn && "pb-8"}`}>
            <div className="whitespace-pre-wrap break-words hyphens-auto w-full react-markdown">
              {renderContent(visibleWords)}
            </div>

            {expandableMessage &&
              message.animationStatus ===
                AssistantMessageAnimationStatus.DISPLAYED && (
                <div
                  className="bg-[#D0D4E4] dark:bg-[#131517]
            border border-[#656A6E40] dark:border-[#373B40]
            rounded-lg mt-4 overflow-hidden"
                >
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className={`w-full flex items-center justify-between 
                  gap-1 p-4 ${isExpanded && "bg-[#C4C8D9] dark:bg-[#202428]"}`}
                  >
                    <div className="flex items-center gap-4">
                      {expandableMessage.icon}
                      <span className="text-[#303535EB] dark:text-[#76797E]">
                        {isExpanded
                          ? expandableMessage.expandBtnText.contentVisibleText
                          : expandableMessage.expandBtnText.contentHiddenText}
                      </span>
                    </div>
                    <LuChevronDown
                      size={24}
                      className={`transition-transform duration-300 transform ${
                        isExpanded ? "-rotate-180" : "rotate-0"
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-y-auto overflow-x-hidden
                  transition-all duration-500 ease-in-out mx-4 ${
                    isExpanded
                      ? "max-h-[1000px] opacity-100 my-4"
                      : "max-h-0 opacity-0 my-0"
                  }`}
                  >
                    <div className={`flex items-start gap-4 w-full min-w-0`}>
                      <span className="break-words hyphens-auto whitespace-pre-wrap flex-1 min-w-0">
                        {isValidHTML(expandableMessage.message)
                          ? parse(expandableMessage.message)
                          : expandableMessage.message}
                      </span>
                      {expandableMessage.canCopy && (
                        <div className="pt-1 flex-shrink-0">
                          <CopyToClipboard
                            text={expandableMessage.message}
                            onCopy={handleCopy}
                          >
                            <LuCopy
                              size={20}
                              className="hover:cursor-pointer hover:opacity-50 transition-opacity duration-300"
                            />
                          </CopyToClipboard>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            {actions &&
              message.animationStatus ===
                AssistantMessageAnimationStatus.DISPLAYED && (
                <div className="mt-4">{actions}</div>
              )}
          </div>
          {rollbackBtn && (
            <button
              className="absolute -bottom-3 -right-4
              flex items-center gap-1 px-3 py-1
              bg-light-surface-primary dark:bg-[#191C1F]
              rounded-md border
              border-white dark:border-[#373B40]
              hover:brightness-150
              transition-all duration-300"
              onClick={rollbackBtn.onClick}
              disabled={rollbackBtn.isDisabled}
            >
              <LuRotateCcw size={14} />
              <span className="text-sm">Rollback to checkpoint</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
